import classNames from 'classnames';
import { isPast } from 'date-fns';
import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';
import { Image, Trash } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { useServerErrors } from '../../../Shared/api';
import ImageCropper from '../../../Shared/Image/ImageCropper';
import { useLocale } from '../../../Shared/locale';
import { donationFields, Event, Form, Locale, Ticket, User } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import { DateInput } from '../../../Shared/UI/DateInput';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { FileInput } from '../../../Shared/UI/FileInput';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputDescription from '../../../Shared/UI/InputDescription';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { Select } from '../../../Shared/UI/Select';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { Textarea } from '../../../Shared/UI/Textarea';
import { Charity, Project } from '../../types';

export interface EditProjectFormProps extends Form {
  project: Project & {
    event: (Event & {
      tickets: Ticket[];
    }) | null;
    new_campaign_notification_users: User[];
    team_share_excluded_tickets: Ticket[];
  };
  users: User[];
  charity: Charity;
}

export default function EditProjectForm({
  project,
  users,
  charity,
  onSubmit,
  serverErrors,
  loading,
}: EditProjectFormProps) {
  const { t } = useTranslation();
  const { parseDate, formatDate } = useLocale();

  const form = useForm({
    defaultValues: {
      title: project.title,
      description: project.description,
      image: null,
      delete_image: !project.image_url,
      mail_sender: project.mail_sender,
      mail_header: null,
      delete_mail_header: false,
      website: project.website,
      email: project.email,
      fields: project.fields,
      required_fields: project.required_fields,
      donation_fields: project.donation_fields,
      terms_url: project.terms_url,
      donation_message: project.donation_message,
      open: project.open,
      allow_individuals: project.allow_individuals,
      allow_teams: project.allow_teams,
      private: project.private,
      team_share_excluded_tickets: project.team_share_excluded_tickets.map((ticket) => ticket.id),
      locale: project.locale,
      campaign_code: project.campaign_code,
      payment_reference: project.payment_reference,
      end_date: project.end_date ? formatDate(project.end_date, 'internal_date') : null,
      new_campaign_notification_users: project.new_campaign_notification_users.map((user) => user.id),
    },
  });

  const {
    register, setValue, handleSubmit, setError, resetField, watch, control,
    formState: { errors, defaultValues },
  } = form;

  useServerErrors(form, serverErrors);

  const [image, setImage] = useState<File>();
  const [croppedImage, setCroppedImage] = useState<File>();
  const deleteImage = watch('delete_image');
  const canDeleteImage = !deleteImage && (!!image || !!project.image_url);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setImage(event.target.files?.[0] || undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', false, { shouldDirty: true });
  };

  const handleCropChange = (file: File, element: HTMLImageElement) => {
    if (element.naturalWidth < 800 || element.naturalHeight < 450) {
      resetImage();
      setError('image', { message: t('shared:validation.image_too_small') });
    } else {
      setCroppedImage(file);
    }
  };

  const resetImage = () => {
    setImage(undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', true, { shouldDirty: true });
  };

  const currentMailHeaderUrl = project.mail_header_url || charity.mail_header_url;
  const [mailHeader, setMailHeader] = useState<File>();
  const deleteMailHeader = watch('delete_mail_header');
  const canDeleteMailHeader = !deleteMailHeader && (!!mailHeader || !!project.mail_header_url);

  const handleMailHeaderChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMailHeader(event.target.files?.[0] || undefined);
    resetField('mail_header'); // Reset errors
    setValue('delete_mail_header', false);
    setMailHeaderSrc(undefined);
  };

  const unsetMailHeader = () => {
    setMailHeader(undefined);
    resetField('mail_header'); // Reset errors
    setValue('delete_mail_header', true);
    setMailHeaderSrc(undefined);
  };

  const [mailHeaderSrc, setMailHeaderSrc] = useState<string>();

  useEffect(() => {
    if (mailHeader) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setMailHeaderSrc(reader.result?.toString() || undefined));
      reader.readAsDataURL(mailHeader);
    }
  }, [mailHeader]);

  const validateMailHeader = (event: SyntheticEvent<HTMLImageElement>) => {
    const element = event.currentTarget;

    if (element.naturalWidth < 570) {
      unsetMailHeader();
      setError('mail_header', { message: t('shared:validation.image_too_small') });
    }
  };

  const allowIndividuals = watch('allow_individuals');
  const allowTeams = watch('allow_teams');

  const enabledFields = watch('fields');
  const enabledRequiredFields = watch('required_fields');
  const enabledDonationFields = watch('donation_fields');

  const open = watch('open');
  const endDate = watch('end_date');
  const parsedEndDate = endDate ? parseDate(endDate, 'internal_date') : null;
  const endDateIsPast = !!parsedEndDate && isPast(parsedEndDate);

  const paymentReference = watch('payment_reference');
  const paymentReferenceLength = paymentReference?.length || 0;
  const maxPaymentReferenceLength = 20;

  const projectTickets = project.event?.tickets || [];

  const submit = async (data: typeof defaultValues) => {
    await onSubmit({
      ...data,
      image: (image && croppedImage) || null,
      mail_header: mailHeader,
      required_fields: data?.required_fields?.filter((field) => data?.fields?.includes(field)),
      end_date: data?.end_date,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_project_settings')}
          </h3>
        </div>

        {serverErrors && (
          <div>
            <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />
          </div>
        )}

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:project')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel required valid={!errors.title} htmlFor="edit_project_title">
                {t('backend:project_title')}
              </InputLabel>
              <Input
                {...register('title', { required: true })}
                id="edit_project_title"
                aria-invalid={!!errors.title}
              />
              <ErrorMessage error={errors.title} attribute={t('backend:project_title')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_description">
                {t('backend:project_description')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_description_description')}
              </InputDescription>
              <Textarea
                {...register('description')}
                id="edit_project_description"
                aria-invalid={!!errors.title}
                rows={3}
              />
              <ErrorMessage error={errors.description} attribute={t('backend:project_description')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_image">
                {t('backend:image')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_image_description')}
              </InputDescription>
              <div className="max-w-[610px] p-4 border rounded-md border-slate-250">
                <div
                  className="flex items-center justify-center mb-4 transition-all rounded-sm bg-slate-50 aspect-video"
                >
                  {!image && !deleteImage && project.image_url && (
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="object-cover w-full h-full rounded-sm"
                    />
                  )}
                  {image && (
                    <ImageCropper
                      image={image}
                      onChange={handleCropChange}
                      aspect={16 / 9}
                    />
                  )}
                  {((!image && !project.image_url) || deleteImage) && (
                    <Icon className="!w-12 !h-12 text-slate-250">
                      <Image strokeWidth={1} />
                    </Icon>
                  )}
                </div>
                <div className="flex space-x-2">
                  <FileInput
                    {...register('image')}
                    accept=".jpg,.jpeg,.png"
                    id="edit_image"
                    aria-invalid={!!errors.image}
                    onChange={handleImageChange}
                  >
                    <Icon className="mr-1">
                      <Image />
                    </Icon>
                    {t('shared:form.select')}
                  </FileInput>
                  {canDeleteImage && (
                    <Button onClick={() => resetImage()} variant="tertiary" size="sm">
                      <Icon className="mr-1">
                        <Trash />
                      </Icon>
                      {t('shared:form.delete')}
                    </Button>
                  )}
                </div>
              </div>
              <ErrorMessage error={errors.image} attribute={t('backend:image')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_website">
                {t('backend:website')}
              </InputLabel>
              <Input
                {...register('website')}
                id="edit_project_website"
                aria-invalid={!!errors.website}
              />
              <ErrorMessage error={errors.website} attribute={t('backend:website')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_donation_message">
                {t('backend:donation_message')}
              </InputLabel>
              <InputDescription>
                {t('backend:donation_message_description')}
              </InputDescription>
              <Textarea
                {...register('donation_message')}
                id="edit_project_donation_message"
                aria-invalid={!!errors.donation_message}
                rows={3}
              />
              <ErrorMessage error={errors.donation_message} attribute={t('backend:donation_message')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:fields')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.fields} htmlFor="edit_project_fields">
                {t('backend:project_fields')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_fields_description')}
              </InputDescription>
              <table className="w-full border border-collapse border-slate-200 sm:w-1/2">
                <tbody>
                  {project.allowed_fields.map((campaignField) => (
                    <tr key={campaignField}>
                      <td className="w-2/3 p-2 border border-slate-200">
                        <CheckboxLabel htmlFor={`edit_project_fields_${campaignField}`}>
                          <Checkbox
                            {...register('fields')}
                            value={campaignField}
                            id={`edit_project_fields_${campaignField}`}
                            className="mt-1"
                          />
                          <span className={classNames(enabledFields.includes(campaignField) && 'font-medium')}>
                            {t(`backend:campaign_fields.${campaignField}`)}
                          </span>
                        </CheckboxLabel>
                      </td>
                      <td className="p-2 border border-slate-200">
                        <CheckboxLabel htmlFor={`edit_project_required_fields_${campaignField}`} >
                          <Checkbox
                            {...register('required_fields')}
                            value={campaignField}
                            checked={enabledFields.includes(campaignField) && enabledRequiredFields.includes(campaignField)}
                            disabled={!enabledFields.includes(campaignField)}
                            id={`edit_project_required_fields_${campaignField}`}
                          />
                          <span className={classNames(!enabledFields.includes(campaignField) && 'opacity-50')}>
                            {t('backend:required')}
                          </span>
                        </CheckboxLabel>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.fields} htmlFor="edit_project_donation_fields">
                {t('backend:project_donation_fields')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_donation_fields_description')}
              </InputDescription>
              <table className="w-full border border-collapse border-slate-200 sm:w-1/2">
                <tbody>
                  {donationFields.map((donationField) => (
                    <tr key={donationField}>
                      <td className="w-2/3 p-2 border border-slate-200">
                        <CheckboxLabel htmlFor={`edit_project_donation_fields_${donationField}`}>
                          <Checkbox
                            {...register('donation_fields')}
                            value={donationField}
                            id={`edit_project_donation_fields_${donationField}`}
                            className="mt-1"
                          />
                          <span className={classNames(enabledDonationFields.includes(donationField) && 'font-medium')}>
                            {t(`backend:donation_fields.${donationField}`)}
                          </span>
                        </CheckboxLabel>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_terms">
                {t('backend:terms_and_conditions')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_terms_and_conditions_description')}
              </InputDescription>
              <Input
                {...register('terms_url')}
                id="edit_project_terms"
                aria-invalid={!!errors.terms_url}
                placeholder="https://"
              />
              <ErrorMessage error={errors.terms_url} attribute={t('backend:terms_and_conditions')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:mails')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel htmlFor="edit_project_email">
                {t('backend:email_address')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_email_description')}
              </InputDescription>
              <Input
                {...register('email')}
                id="edit_project_email"
                aria-invalid={!!errors.email}
                placeholder={charity.email || ''}
              />
              <ErrorMessage error={errors.email} attribute={t('backend:email_address')} />
            </InputGroup>
            <InputGroup>
              <InputLabel htmlFor="edit_project_mail_sender">
                {t('backend:mail_sender')}
              </InputLabel>
              <Input
                {...register('mail_sender')}
                id="edit_project_mail_sender"
                aria-invalid={!!errors.mail_sender}
                placeholder={charity?.mail_sender || charity.title}
              />
              <ErrorMessage error={errors.mail_sender} attribute={t('backend:mail_sender')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_project_header">
                {t('backend:project_mail_header')}
              </InputLabel>
              <InputDescription>
                {t('backend:project_mail_header_description')}
              </InputDescription>
              <div className="max-w-[604px] p-4 border rounded-md border-slate-250">
                <div
                  className="flex items-center justify-center mb-4 transition-all rounded-sm bg-slate-50"
                >
                  {!mailHeader && !deleteMailHeader && currentMailHeaderUrl && (
                    <img
                      src={currentMailHeaderUrl}
                      alt={project.title}
                      className="w-full rounded-sm"
                    />
                  )}
                  {mailHeader && mailHeaderSrc && (
                    <img
                      src={mailHeaderSrc}
                      alt={project.title}
                      className="w-full rounded-sm"
                      onLoad={validateMailHeader}
                    />
                  )}
                  {((!mailHeader && !currentMailHeaderUrl) || deleteMailHeader) && (
                    <Icon className="!w-12 !h-12 text-slate-250 my-16">
                      <Image strokeWidth={1} />
                    </Icon>
                  )}
                </div>
                <div className="flex space-x-2">
                  <FileInput
                    {...register('mail_header')}
                    accept=".jpg,.jpeg,.png"
                    id="edit_project_mail_header"
                    aria-invalid={!!errors.mail_header}
                    onChange={handleMailHeaderChange}
                  >
                    <Icon className="mr-1">
                      <Image />
                    </Icon>
                    {t('shared:form.select')}
                  </FileInput>
                  {canDeleteMailHeader && (
                    <Button onClick={() => unsetMailHeader()} variant="tertiary" size="sm">
                      <Icon className="mr-1">
                        <Trash />
                      </Icon>
                      {t('shared:form.delete')}
                    </Button>
                  )}
                </div>
              </div>
              <ErrorMessage error={errors.mail_header} attribute={t('backend:project_mail_header')} />
            </InputGroup>
          </div>
        </div>

        {!project.default && (
          <div className="grid grid-cols-[1fr_3fr] gap-8">
            <div>
              <h4 className="font-medium text-slate-500 mb-1">
                {t('backend:restrictions')}
              </h4>
            </div>
            <div className="space-y-6">
              <InputGroup>
                <InputLabel valid={!errors.allow_individuals && !errors.allow_teams}>
                  {t('backend:campaign_types')}
                </InputLabel>
                <InputDescription>
                  {t('backend:allowed_campaign_types_description')}
                </InputDescription>
                <CheckboxLabel htmlFor="edit_project_allow_individuals">
                  <Checkbox
                    {...register('allow_individuals')}
                    id="edit_project_allow_individuals"
                    disabled={allowIndividuals && !allowTeams}
                    className="mt-1"
                  />
                  <span>
                    {t('backend:individual_campaigns')}
                  </span>
                </CheckboxLabel>
                <CheckboxLabel htmlFor="edit_project_allow_teams">
                  <Checkbox
                    {...register('allow_teams')}
                    id="edit_project_allow_teams"
                    disabled={!allowIndividuals && allowTeams}
                    className="mt-1"
                  />
                  <span>
                    {t('backend:team_campaigns')}
                  </span>
                </CheckboxLabel>
              </InputGroup>

              <InputGroup>
                <InputLabel valid={!errors.private} htmlFor="edit_project_private">
                  {t('backend:private_project')}
                </InputLabel>
                <InputDescription>
                  {t('backend:private_project_description')}
                </InputDescription>
                <CheckboxLabel htmlFor="edit_project_private">
                  <Checkbox
                    {...register('private')}
                    id="edit_project_private"
                    className="mt-1"
                  />
                  <span>
                    {t('backend:private_project_label')}
                  </span>
                </CheckboxLabel>
              </InputGroup>

              {projectTickets.length > 0 && (
                <InputGroup>
                  <InputLabel valid={!errors.team_share_excluded_tickets}>
                    {t('backend:team_share_exclusions')}
                  </InputLabel>
                  <InputDescription>
                    {t('backend:team_share_exclusions_description')}
                  </InputDescription>
                  {projectTickets.map((ticket) => (
                    <CheckboxLabel htmlFor={`team_share_excluded_ticket_${ticket.id}`} key={ticket.id}>
                      <Checkbox
                        {...register('team_share_excluded_tickets')}
                        value={ticket.id}
                        id={`team_share_excluded_ticket_${ticket.id}`}
                        className="mt-1"
                      />
                      <span>
                        {ticket.title}
                      </span>
                    </CheckboxLabel>
                  ))}
                </InputGroup>
              )}

              <InputGroup>
                <InputLabel valid={!errors.end_date} htmlFor="edit_project_end_date">
                  {t('backend:project_end_date')}
                </InputLabel>
                <InputDescription>
                  {t('backend:project_end_date_description')}
                </InputDescription>
                <div className="w-64">
                  <Controller
                    control={control}
                    name="end_date"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <DateInput
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        ariaInvalid={!!errors.end_date}
                        optional
                        id="edit_project_end_date"
                      />
                    )}
                  />
                </div>
                <ErrorMessage attribute={t('backend:project_end_date')} error={errors.end_date} />
              </InputGroup>

              <InputGroup>
                <InputLabel valid={!errors.open} htmlFor="edit_project_open">
                  {t('backend:open_project')}
                </InputLabel>
                <InputDescription>
                  {t('backend:open_project_description')}
                </InputDescription>
                <CheckboxLabel htmlFor="edit_project_open">
                  <Checkbox
                    checked={endDateIsPast ? false : open}
                    onChange={(event) => setValue('open', event.target.checked)}
                    disabled={endDateIsPast}
                    id="edit_project_open"
                    className="mt-1"
                  />
                  <span className={classNames(endDateIsPast && 'opacity-50')}>
                    {t('backend:open_project_label')}
                  </span>
                </CheckboxLabel>
              </InputGroup>
            </div>
          </div>
        )}

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:language')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel required valid={!errors.locale} htmlFor="edit_project_locale">
                {t('backend:language')}
              </InputLabel>
              <Select
                {...register('locale', { required: true })}
                id="edit_project_locale"
                aria-invalid={!!errors.locale}
              >
                <option value={Locale.EN}>
                  {t(`shared:locales.${Locale.EN}`)}
                </option>
                <option value={Locale.NL}>
                  {t(`shared:locales.${Locale.NL}`)}
                </option>
              </Select>
              <ErrorMessage error={errors.locale} attribute={t('backend:language')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:payments')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.campaign_code} htmlFor="edit_project_campaign_code">
                {t('backend:campaign_code')}
              </InputLabel>
              <InputDescription>
                {t('backend:campaign_code_description')}
              </InputDescription>
              <Input
                {...register('campaign_code')}
                id="edit_project_campaign_code"
                aria-invalid={!!errors.campaign_code}
              />
              <ErrorMessage attribute={t('backend:campaign_code')} error={errors.campaign_code} />
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.payment_reference} htmlFor="edit_project_payment_reference">
                {t('backend:payment_reference')}
              </InputLabel>
              <InputDescription>
                {t('backend:payment_reference_description')}
                {' '}
                <span className={classNames('bg-blue-200 text-blue-700 px-1 inline-block text-sm rounded-sm', paymentReferenceLength === 0 && 'opacity-50')}>
                  <strong>{paymentReferenceLength}</strong> / {maxPaymentReferenceLength}
                </span>
              </InputDescription>
              <Input
                {...register('payment_reference')}
                id="edit_project_payment_reference"
                maxLength={maxPaymentReferenceLength}
                aria-invalid={!!errors.payment_reference}
                placeholder={charity.payment_reference || ''}
              />
              <div className="mt-2">
                <Trans i18nKey="backend:example_payment_reference" values={{ reference: `${paymentReference || charity.payment_reference || ''} 12345 ${project.title} ${charity.title}` }}>
                  <span className="font-medium text-slate-500"/>
                </Trans>
              </div>
              <ErrorMessage attribute={t('backend:payment_reference')} error={errors.payment_reference} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:notifications')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.new_campaign_notification_users}>
                {t('backend:new_campaign_notification')}
              </InputLabel>
              <InputDescription>
                {t('backend:new_campaign_notification_description')}
              </InputDescription>
              {users.map((user) => (
                <CheckboxLabel htmlFor={`user_${user.id}`} key={user.id}>
                  <Checkbox
                    {...register('new_campaign_notification_users')}
                    value={user.id}
                    id={`user_${user.id}`}
                    className="mt-1"
                  />
                  <span>
                    {user.full_name}
                  </span>
                </CheckboxLabel>
              ))}
            </InputGroup>
          </div>
        </div>

        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}
